/**
 * D-020 LT2-003 — the two-sided expanded ladder for a derivative line.
 *
 * Replaces the single-side `LadderRowsFor` in the compact spread/total
 * expanded slot when the trader has lt2Access: BOTH executable sides render
 * as adjacent labeled columns — YES/OVER on the LEFT, NO/UNDER on the RIGHT
 * (this left/right order is also the A/D hotkey contract), each with 3
 * mirrored asks over 3 bids from the shared `buildSideLevels` math, own
 * resting-order chips, and click-to-trade levels. The trader can alternate
 * sides without any toggle or panel transition.
 *
 * Own-order injection (LT2-009): a confirmed resting order whose price has no
 * book level yet — a just-accepted new top of book, before the feed ticks —
 * renders as a dashed injected level showing ONLY the trader's own size, so
 * an accepted order is never invisible. Injection never fabricates market
 * depth: the size shown is the order's own remaining count, marked as yours.
 *
 * Clicks are inert unless the line's availability is 'active' — the same
 * classifier the pills use, so a suspended/stale line can't accept an order
 * through its ladder either.
 */
import { cn } from '@/lib/infra/utils';
import { Td } from '@/components/atoms/TableCells';
import type { BookLevel, VenueBook } from '../ValueDashboard/types';
import type { UserOrderSlice } from '@/hooks/useUserKalshiPOS';
import type { LineAvailability } from '@/lib/sport/lineAvailability';
import { americanOdds } from '../ValueDashboard/tradeModalBook';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useFeeDisplayStatus, useFeeFor } from '@/services/hydraFees';
import {
  FEE_DISPLAY_MESSAGE,
  FeeScheduleMarker,
  feeDisplayCanHide,
} from '@/components/molecules/FeeScheduleMarker';
import {
  buildSideLevels,
  formatVolume,
  ladderRowPricePresentation,
  queueDepth,
  orderTooltip,
  type BidRowOrder,
} from '../ValueDashboard/ladder/helpers';
import { RestingOrderGroup } from '../ValueDashboard/ladder/RestingOrderGroup';

export interface TwoSidedLadderProps {
  kalshi: VenueBook | null;
  /** Column headers — "YES"/"NO" for spreads, "OVER"/"UNDER" for totals. */
  yesLabel: string;
  noLabel: string;
  /** The trader's open orders on this line's ticker (mergeOrdersForTicker
   *  output); null when there is no Kalshi book. */
  userOrders: UserOrderSlice[] | null;
  availability: LineAvailability;
  /** Maker-fee rebate fraction (0..1) folded into bid-row odds. */
  makerRebatePct: number;
  /** Level click → the parent maps to onPillTrade with
   *  {origin:'ladder', kind} so maker semantics survive. */
  onLevelClick: (side: 'yes' | 'no', kind: 'bid' | 'ask', cents: number) => void;
  /** Host table column count for the wrapper cell. */
  colSpan?: number;
  /** D-020 V2B — paints the hotkey-focus treatment on the expanded slot so
   *  the focused card reads as one visual unit with its action row. */
  focused?: boolean;
  /** #609 — the focused card's id, so the expanded slot can carry the hotkey
   *  fence's marks. This row is where the submittable price is DISPLAYED once
   *  the card is expanded (the resolver reads the join bid from here, not from
   *  the action row's pills), so the fence tests THIS row's visibility. Null
   *  whenever hotkeys are off — the marks must never appear then. */
  lt2FocusId?: string | null;
  /** D-020 LT2-018 — render the NO column LEFT (team-side toggle display
   *  order). Each column's side/labels/clicks are untouched. */
  flipColumns?: boolean;
}

interface LevelRow {
  level: BookLevel;
  /** True when this level exists only because the trader's own confirmed
   *  resting order sits there and the book hasn't shown it yet (LT2-009). */
  injected: boolean;
}

/** Merge the trader's own resting-buy levels that are absent from the book's
 *  bid rows, sorted best-first. Own size only — never fabricated depth. */
function withInjectedOwnLevels(
  bids: BookLevel[],
  ownBuyOrders: UserOrderSlice[],
): LevelRow[] {
  const present = new Set(bids.map((l) => l.priceCents));
  const rows: LevelRow[] = bids.map((level) => ({ level, injected: false }));
  const injectedByPrice = new Map<number, number>();
  for (const o of ownBuyOrders) {
    if (present.has(o.priceCents)) continue;
    injectedByPrice.set(
      o.priceCents,
      (injectedByPrice.get(o.priceCents) ?? 0) + o.remainingCount,
    );
  }
  for (const [priceCents, size] of injectedByPrice) {
    rows.push({ level: { priceCents, size }, injected: true });
  }
  rows.sort((a, b) => b.level.priceCents - a.level.priceCents);
  return rows;
}

/** The ladder's inner two-column body — a plain div so the multi-line panel
 *  (LT2-017) can lay three of them side by side inside one table row. */
export function TwoSidedLadderBody({
  kalshi,
  yesLabel,
  noLabel,
  userOrders,
  availability,
  makerRebatePct,
  onLevelClick,
  flipColumns = false,
}: Omit<TwoSidedLadderProps, 'colSpan' | 'focused' | 'lt2FocusId'>) {
  const feeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const hideUnknownFeePrices = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const ticker = kalshi?.marketTicker ?? null;
  const feeEntry = useFeeFor(ticker);
  const resolvedFeeStatus = useFeeDisplayStatus(ticker, feeEntry);
  const feeStatus = feeAdjusted ? resolvedFeeStatus : 'not-applicable';
  const hidePrices = hideUnknownFeePrices && feeDisplayCanHide(feeStatus);
  const active = availability === 'active';

  const renderColumn = (side: 'yes' | 'no', label: string) => {
    const { asks, bids } = buildSideLevels(kalshi, side);
    const ownOrders = (userOrders ?? []).filter((o) => o.side === side);
    const ownBuys = ownOrders.filter((o) => o.action === 'buy');
    const ownSells = ownOrders.filter((o) => o.action === 'sell');
    const bidRows = withInjectedOwnLevels(bids, ownBuys);
    // Asks worst-at-top, best-at-bottom (nearest the separator) — same visual
    // convention as the single-side ladder.
    const askRows: LevelRow[] = [...asks]
      .reverse()
      .map((level) => ({ level, injected: false }));

    const chipsFor = (cents: number, action: 'buy' | 'sell'): BidRowOrder[] => {
      const source = action === 'buy' ? ownBuys : ownSells;
      return source
        .filter((o) => o.priceCents === cents)
        .sort((a, b) => b.remainingCount - a.remainingCount)
        .map((o) => ({
          orderId: o.orderId,
          remainingCount: o.remainingCount,
          queueOrBetter: kalshi ? queueDepth(o, kalshi).atLevelOrBetter : null,
          tooltip: kalshi ? orderTooltip(o, kalshi) : '',
        }));
    };

    const renderLevel = (row: LevelRow, kind: 'bid' | 'ask') => {
      const cents = row.level.priceCents;
      const price = ladderRowPricePresentation(cents, kind, feeAdjusted, feeEntry, makerRebatePct);
      const odds = hidePrices ? null : americanOdds(price.effectiveCents);
      const chips = chipsFor(cents, kind === 'bid' ? 'buy' : 'sell');
      const clickable = active && !hidePrices && !row.injected;
      return (
        <div
          key={`${kind}-${cents}`}
          className={cn(
            'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-2 rounded px-1.5 py-0.5',
            clickable && 'cursor-pointer hover:bg-muted/40',
            row.injected && 'border border-dashed border-primary/50',
          )}
          onClick={
            clickable
              ? (e) => {
                  e.stopPropagation();
                  onLevelClick(side, kind, cents);
                }
              : undefined
          }
          title={
            row.injected
              ? 'Your order — accepted and resting; awaiting book confirmation'
              : hidePrices
                ? FEE_DISPLAY_MESSAGE[feeStatus] ?? undefined
                : active
                  ? `Click to ${kind === 'bid' ? 'join (maker)' : 'take'} ${label} @ ${cents}¢`
                  : 'Order entry disabled — market not active'
          }
        >
          <span className="flex items-center gap-1 whitespace-nowrap font-mono text-xs">
            <span className={kind === 'ask' ? 'text-negative' : 'text-positive'}>
              {hidePrices ? '—' : `${price.rawCents}¢`}
            </span>
            {odds && (
              <>
                <span className="text-muted-foreground/40">|</span>
                <span className="text-foreground">{odds}</span>
              </>
            )}
            <FeeScheduleMarker status={feeStatus} />
          </span>
          <span
            className="text-muted-foreground w-[6ch] text-right font-mono text-xs"
            title={
              row.injected
                ? 'Your own resting contracts (book has not shown this level yet)'
                : 'Resting Qty — contracts resting at this price level'
            }
          >
            {formatVolume(row.level.size)}
          </span>
          <span
            className="flex min-w-[3ch] items-center justify-end gap-1 text-[10px] leading-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* #650 — bounded, same component as the two table ladders.
                `level={null}`: #523 named three surfaces — the dashboard
                moneyline ladder, the Spreads/Totals ladder and the compact
                Game Detail ladder — and this one is not among them, so it has
                never offered the level-wide cancel. Quietly adding a
                destructive control here is not this ticket's to make. */}
            <RestingOrderGroup orders={chips} level={null} priceCents={cents} />
          </span>
        </div>
      );
    };

    return (
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            'mb-1 border-b border-border/40 pb-0.5 text-[10px] font-semibold uppercase tracking-wider',
            side === 'yes' ? 'text-positive' : 'text-negative',
          )}
        >
          {label}
          <span className="text-muted-foreground/60 ml-2 normal-case tracking-normal">
            asks / bids
          </span>
        </div>
        <div className="flex flex-col gap-px">
          {askRows.length === 0 && (
            <div className="text-muted-foreground/50 px-1.5 py-0.5 text-[10px]">no asks</div>
          )}
          {askRows.map((row) => renderLevel(row, 'ask'))}
          <div className="border-border/40 my-0.5 border-t" aria-hidden />
          {bidRows.length === 0 && (
            <div className="text-muted-foreground/50 px-1.5 py-0.5 text-[10px]">no bids</div>
          )}
          {bidRows.map((row) => renderLevel(row, 'bid'))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={cn(
        'flex gap-6 px-2',
        !active && 'opacity-60',
        // LT2-018 display-order flip: the NO column renders LEFT. Sides,
        // labels and clicks are untouched.
        flipColumns && 'flex-row-reverse',
      )}
    >
      {renderColumn('yes', yesLabel)}
      {renderColumn('no', noLabel)}
    </div>
  );
}

export function TwoSidedLadder({
  colSpan = 12,
  focused = false,
  lt2FocusId = null,
  ...body
}: TwoSidedLadderProps) {
  const marks = focused && lt2FocusId ? lt2FocusId : undefined;
  return (
    <tr
      className={cn('border-0', focused && '!bg-primary/15')}
      data-testid="two-sided-ladder"
      data-lt2-focused={marks}
      data-lt2-price={marks == null ? undefined : ''}
      data-lt2-hotkey-control={marks == null ? undefined : ''}
    >
      <Td colSpan={colSpan} className="py-1.5">
        <TwoSidedLadderBody {...body} />
      </Td>
    </tr>
  );
}
